import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { MoreVertical, Flag, Ban } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
    AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import ReportDialog from "@/components/ReportDialog";
import { toast } from "sonner";

/**
 * The "•••" menu on a room detail page — exposes Apple-required UGC actions:
 *   – Report listing (opens ReportDialog)
 *   – Block user     (persists the block in the backend, then leaves the page)
 *
 * Both actions are only visible when a real user is logged in AND the viewer
 * isn't looking at their own room.
 */
export default function RoomActionsMenu({ room, currentUser }) {
    const [reportOpen, setReportOpen] = useState(false);
    const [blocking, setBlocking] = useState(false);
    const navigate = useNavigate();

    if (!currentUser || !room || currentUser.id === room.owner_id) return null;

    const block = async () => {
        setBlocking(true);
        try {
            await api.post(`/users/${room.owner_id}/block`);
            toast.success(`${room.owner_name || "User"} has been blocked. You won't see their content anymore.`);
            navigate("/browse");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't block this user");
        } finally {
            setBlocking(false);
        }
    };

    return (
        <>
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <button
                        type="button"
                        aria-label="More actions"
                        data-testid="room-actions-menu-btn"
                        className="w-10 h-10 rounded-full bg-white/95 shadow-md flex items-center justify-center active:scale-95 transition-transform border border-border/60"
                    >
                        <MoreVertical className="w-5 h-5" />
                    </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56" data-testid="room-actions-menu">
                    <DropdownMenuItem
                        data-testid="room-actions-report"
                        onSelect={(e) => { e.preventDefault(); setReportOpen(true); }}
                    >
                        <Flag className="w-4 h-4 mr-2 text-primary" /> Report listing
                    </DropdownMenuItem>
                    <AlertDialog>
                        <AlertDialogTrigger asChild>
                            <DropdownMenuItem
                                data-testid="room-actions-block"
                                onSelect={(e) => { e.preventDefault(); }}
                            >
                                <Ban className="w-4 h-4 mr-2 text-destructive" /> Block user
                            </DropdownMenuItem>
                        </AlertDialogTrigger>
                        <AlertDialogContent data-testid="block-user-dialog">
                            <AlertDialogHeader>
                                <AlertDialogTitle>Block {room.owner_name || "this user"}?</AlertDialogTitle>
                                <AlertDialogDescription>
                                    You won't see their listings or receive messages from them anymore. This action is persistent —
                                    you can unblock them later from your profile.
                                </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                                <AlertDialogCancel data-testid="block-user-cancel">Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                    data-testid="block-user-confirm"
                                    disabled={blocking}
                                    onClick={block}
                                    className="bg-destructive hover:bg-destructive/90"
                                >
                                    {blocking ? "Blocking…" : "Yes, block"}
                                </AlertDialogAction>
                            </AlertDialogFooter>
                        </AlertDialogContent>
                    </AlertDialog>
                </DropdownMenuContent>
            </DropdownMenu>

            <ReportDialog
                targetType="room"
                targetId={room.id}
                targetLabel={`Report "${room.title || "this listing"}"`}
                open={reportOpen}
                onOpenChange={setReportOpen}
            />
        </>
    );
}
